import { UserGroup } from "@/pages/settings/components/userGroups/types";

export const AggregateByValues = {
  Daily: "day",
  Weekly: "week",
  Monthly: "month",
};

export const BarColors = {
  compute: "#D67687",
  storage: "#FAC515",
  serverless: "#7A5AF8",
  total_estimate: "#8390A3",
  compute_estimate: "#8390A3",
  storage_estimate: "#8390A3",
  total_storage_estimate: "#8390A3",
  serverless_estimate: "#8390A3",
  stage: "#EE46BC",
  failsafe: "#EF8161",
  hybrid_table_storage: "#51E5E6",
  total_storage: "#8454FF",
  total: "#7A5AF8",
  search_optimization: "#51E5E6",
  automatic_clustering: "#8454FF",
  materialized_views: "#EF8161",
  snowpipe: "#EE46BC",
  data_transfer: "#FDD835",
  query_acceleration: "#01CD8C",
  snowpark_container_services: "#14B8A6",
  snowpark_container_services_estimate: "#8390A3",
  trust_center: "#FB7185",
  trust_center_estimate: "#8390A3",
  replication: "#F59E0B",
  replication_estimate: "#8390A3",
  sensitive_data_classification: "#22C55E",
  sensitive_data_classification_estimate: "#8390A3",
  data_quality_monitoring: "#C084FC",
  data_quality_monitoring_estimate: "#8390A3",
  reader_data_transfer: "#EC4899",
  reader_data_transfer_estimate: "#8390A3",
  serverless_tasks: "#0EA5E9",
  serverless_tasks_estimate: "#8390A3",
  snowpipe_streaming: "#6366F1",
  snowpipe_streaming_estimate: "#8390A3",
  value: "#01CD8C",
  number_of_decisions: "#9382D9",
  "compute cost": "#5B9FFF",
  "cloud services cost": "#FF754C",
  "query acceleration cost": "#FFCE73",
  cloud_services: "#247EFE",
  adj_for_incl_cloud_services: "#FAC515",
  cloud_services_estimate: "#8390A3",
  adj_for_incl_cloud_services_estimate: "#FAC515",
  ai_services: "#01CD8C",
  ai_services_estimate: "#8390A3",
  document_ai: "#EE46BC",
  document_ai_estimate: "#8390A3",
  functions: "#FAC515",
  functions_estimate: "#8390A3",
  fine_tuning: "#01CD8C",
  fine_tuning_estimate: "#8390A3",
  analyst: "#5B9FFF",
  analyst_estimate: "#8390A3",
  search: "#8454FF",
  search_estimate: "#8390A3",
  search_serving: "#EE46BC",
  search_serving_estimate: "#8390A3",
  search_daily_aggregate: "#A66DFF",
  search_daily_aggregate_estimate: "#8390A3",
  cortex_code_cli: "#EF8161",
  cortex_code_cli_estimate: "#8390A3",
  cortex_agent: "#51E5E6",
  cortex_agent_estimate: "#8390A3",
  snowflake_intelligence: "#8454FF",
  snowflake_intelligence_estimate: "#8390A3",
  ai_inference: "#3CD4FF",
  ai_inference_estimate: "#8390A3",
  ai_services_other: "#9CA3AF",
  ai_services_other_estimate: "#8390A3",
  cortex_code_snowsight: "#FF7A6B",
  cortex_code_snowsight_estimate: "#8390A3",
  cortex_code_desktop: "#F472B6",
  cortex_code_desktop_estimate: "#8390A3",
  reader_ai_services: "#FFB547",
  reader_ai_services_estimate: "#8390A3",
  reader_ai_inference: "#34D399",
  reader_ai_inference_estimate: "#8390A3",
  reader_cortex_code_snowsight: "#A78BFA",
  reader_cortex_code_snowsight_estimate: "#8390A3",
  other: "#9CA3AF",
  other_estimate: "#8390A3",
};

// Deterministic-hash palette for keys not in BarColors (dynamic "Other"
// sub-bars). Sized at 24 distinct hex values that are NOT already used in
// BarColors so a hashed fallback can't visually collide with a top-line
// known category. Collision probability is still O(k²/n) — at k=5 it's
// ~22% (better than 62% with a 12-slot palette) and the visual contract
// is "best-effort, may collide above ~6 unmapped keys"; the docstring
// on getBarColor captures that explicitly.
const FALLBACK_PALETTE = [
  "#2563EB", // blue-600
  "#DC2626", // red-600
  "#16A34A", // green-600
  "#9333EA", // purple-600
  "#EA580C", // orange-600
  "#0891B2", // cyan-600
  "#CA8A04", // yellow-600
  "#BE185D", // pink-700
  "#65A30D", // lime-600
  "#0EA5E9", // sky-500
  "#7C3AED", // violet-600
  "#E11D48", // rose-600
  "#059669", // emerald-600
  "#D946EF", // fuchsia-500
  "#475569", // slate-600
  "#0D9488", // teal-600
  "#B45309", // amber-700
  "#4F46E5", // indigo-600
  "#C2410C", // orange-700
  "#9F1239", // rose-800
  "#15803D", // green-700
  "#1E40AF", // blue-800
  "#86198F", // fuchsia-800
  "#7F1D1D", // red-900
] as const;

const hashCode = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

/**
 * Resolve a chart-bar colour for the given series key.
 *
 * Known keys come from the hand-tuned BarColors map. Unknown keys —
 * typically a new Snowflake usage_type surfaced via the Other-tab
 * catchall path — fall back to a deterministic slot in
 * FALLBACK_PALETTE. The fallback is best-effort: with 24 slots
 * collisions stay improbable below ~6 simultaneous unmapped keys.
 * Above that, two unrelated sub-bars may render the same colour
 * until someone promotes the key into BarColors.
 */
export const getBarColor = (key: string): string =>
  BarColors[key as keyof typeof BarColors] ??
  FALLBACK_PALETTE[hashCode(key) % FALLBACK_PALETTE.length];

const TotalCostEntitiesBase: Record<
  string,
  {
    fields: {
      key: { current: string; estimate: string };
      tabKey?: string;
      label: string;
      showChangePercentage?: boolean;
      hideSticker?: boolean;
      isTotal?: boolean;
      hideCurrency?: boolean;
      hideWhenZero?: boolean;
    }[];
  }
> = {
  overall: {
    fields: [
      {
        key: { current: "total", estimate: "total_estimate" },
        tabKey: "overall",
        label: "total",
        hideSticker: true,
        isTotal: true,
      },
      {
        key: { current: "compute", estimate: "compute_estimate" },
        label: "compute",
        showChangePercentage: true,
      },
      {
        key: { current: "storage", estimate: "storage_estimate" },
        label: "storage",
        showChangePercentage: true,
      },
      {
        key: { current: "serverless", estimate: "serverless_estimate" },
        label: "serverless",
        showChangePercentage: true,
      },
      {
        key: { current: "cloud_services", estimate: "cloud_services_estimate" },
        label: "cloud_services",
        showChangePercentage: true,
      },
      {
        key: { current: "other", estimate: "other_estimate" },
        label: "other",
        showChangePercentage: true,
        hideWhenZero: true,
      },
    ],
  },
  compute: {
    fields: [
      {
        key: { current: "compute", estimate: "compute_estimate" },
        label: "compute",
        showChangePercentage: true,
      },
      {
        key: { current: "compute", estimate: "compute_estimate" },
        label: "compute",
        isTotal: true,
        showChangePercentage: true,
      },
    ],
  },
  storage: {
    fields: [
      {
        key: { current: "storage", estimate: "storage_estimate" },
        label: "storage",
        showChangePercentage: true,
      },
      {
        key: { current: "stage", estimate: "stage_estimate" },
        label: "stage",
        showChangePercentage: true,
      },
      {
        key: { current: "failsafe", estimate: "failsafe_estimate" },
        label: "failsafe",
        showChangePercentage: true,
      },
      {
        key: {
          current: "hybrid_table_storage",
          estimate: "hybrid_table_storage_estimate",
        },
        label: "hybrid_table_storage",
        showChangePercentage: true,
      },
      {
        key: { current: "total_storage", estimate: "total_storage_estimate" },
        label: "total_storage",
        isTotal: true,
        showChangePercentage: true,
      },
    ],
  },
  serverless: {
    fields: [
      {
        key: { current: "total", estimate: "total_estimate" },
        label: "serverless",
        isTotal: true,
        hideSticker: true,
      },
      {
        key: {
          current: "search_optimization",
          estimate: "search_optimization_estimate",
        },
        label: "search_optimization",
        showChangePercentage: true,
      },
      {
        key: {
          current: "automatic_clustering",
          estimate: "automatic_clustering_estimate",
        },
        label: "automatic_clustering",
        showChangePercentage: true,
      },
      {
        key: {
          current: "materialized_views",
          estimate: "materialized_views_estimate",
        },
        label: "materialized_views",
        showChangePercentage: true,
      },
      {
        key: { current: "snowpipe", estimate: "snowpipe_estimate" },
        label: "snowpipe",
        showChangePercentage: true,
      },
      {
        key: { current: "data_transfer", estimate: "data_transfer_estimate" },
        label: "data_transfer",
        showChangePercentage: true,
      },
      {
        key: {
          current: "query_acceleration",
          estimate: "query_acceleration_estimate",
        },
        label: "query_acceleration",
        showChangePercentage: true,
      },
      // Newer Snowflake serverless billing lines. Hidden when zero so each
      // tenant only sees what it actually uses. The trailing `other` row is
      // derived in CostBreakdown.tsx and absorbs low-materiality categories
      // not enumerated here.
      {
        key: {
          current: "snowpark_container_services",
          estimate: "snowpark_container_services_estimate",
        },
        label: "snowpark_container_services",
        showChangePercentage: true,
        hideWhenZero: true,
      },
      {
        key: { current: "trust_center", estimate: "trust_center_estimate" },
        label: "trust_center",
        showChangePercentage: true,
        hideWhenZero: true,
      },
      {
        key: { current: "replication", estimate: "replication_estimate" },
        label: "replication",
        showChangePercentage: true,
        hideWhenZero: true,
      },
      {
        key: {
          current: "sensitive_data_classification",
          estimate: "sensitive_data_classification_estimate",
        },
        label: "sensitive_data_classification",
        showChangePercentage: true,
        hideWhenZero: true,
      },
      {
        key: {
          current: "data_quality_monitoring",
          estimate: "data_quality_monitoring_estimate",
        },
        label: "data_quality_monitoring",
        showChangePercentage: true,
        hideWhenZero: true,
      },
      {
        key: {
          current: "reader_data_transfer",
          estimate: "reader_data_transfer_estimate",
        },
        label: "reader_data_transfer",
        showChangePercentage: true,
        hideWhenZero: true,
      },
      {
        key: { current: "serverless_tasks", estimate: "serverless_tasks_estimate" },
        label: "serverless_tasks",
        showChangePercentage: true,
        hideWhenZero: true,
      },
      {
        key: { current: "snowpipe_streaming", estimate: "snowpipe_streaming_estimate" },
        label: "snowpipe_streaming",
        showChangePercentage: true,
        hideWhenZero: true,
      },
      {
        key: { current: "other", estimate: "other_estimate" },
        label: "other",
        showChangePercentage: true,
      },
    ],
  },
  cloud_services: {
    fields: [
      {
        key: { current: "cloud_services", estimate: "cloud_services_estimate" },
        label: "cloud_services",
        showChangePercentage: true,
      },
      {
        key: {
          current: "adj_for_incl_cloud_services",
          estimate: "adj_for_incl_cloud_services_estimate",
        },
        label: "adjusted_cloud_services",
        isTotal: false,
        showChangePercentage: true,
      },
      {
        key: { current: "total", estimate: "total_estimate" },
        label: "cloud_services",
        isTotal: true,
        hideSticker: true,
      },
    ],
  },
  autonomous: {
    fields: [
      {
        key: {
          current: "value",
          estimate: "value_estimate",
        },
        label: "Autonomous savings",
      },
      {
        key: {
          current: "number_of_decisions",
          estimate: "number_of_decisions_estimate",
        },
        label: "Agent Decisions",
        hideCurrency: true,
      },
    ],
  },
  warehouse: {
    fields: [
      {
        key: { current: "total cost", estimate: "total_estimate" },
        label: "total",
        hideSticker: true,
        isTotal: true,
        showChangePercentage: true,
      },
      {
        key: { current: "compute cost", estimate: "" },
        label: "compute cost",
        showChangePercentage: true,
      },
      {
        key: { current: "cloud services cost", estimate: "" },
        label: "cloud services cost",
        showChangePercentage: true,
      },
      {
        key: { current: "query acceleration cost", estimate: "" },
        label: "query acceleration cost",
        showChangePercentage: true,
      },
    ],
  },
  // Sub-bar fields are derived from the response at render time
  // (see `otherInlineFields` in CostBreakdown.tsx).
  other: {
    fields: [
      {
        key: { current: "total", estimate: "total_estimate" },
        label: "other",
        isTotal: true,
        hideSticker: true,
      },
    ],
  },
};

export const TotalCostEntities = (
  rbacEnabled = false,
  userGroup: UserGroup | null = null
): Record<
  string,
  {
    fields: {
      key: { current: string; estimate: string };
      tabKey?: string;
      label: string;
      showChangePercentage?: boolean;
      hideSticker?: boolean;
      isTotal?: boolean;
      hideCurrency?: boolean;
      hideWhenZero?: boolean;
    }[];
  }
> => {
  // Create a deep copy to avoid mutating the base object
  const totalCostEntities = JSON.parse(JSON.stringify(TotalCostEntitiesBase));

  const hasNoAccess = rbacEnabled && Boolean(userGroup);

  // Always ensure ai_services field is in overall
  if (
    !totalCostEntities.overall.fields.find(
      (field: { label: string }) => field.label === "ai_services"
    )
  ) {
    totalCostEntities.overall.fields.push({
      key: { current: "ai_services", estimate: "ai_services_estimate" },
      label: "ai_services",
      showChangePercentage: true,
    });

    // Always regenerate ai_services fields based on current access level
    const allAiServicesFields = [
      {
        key: { current: "total", estimate: "total_estimate" },
        label: "ai_services",
        isTotal: true,
        hideSticker: true,
      },
      {
        key: { current: "functions", estimate: "functions_estimate" },
        label: "cortex_function",
        showChangePercentage: true,
      },
      {
        key: { current: "ai_inference", estimate: "ai_inference_estimate" },
        label: "ai_inference",
        showChangePercentage: true,
      },
      {
        key: {
          current: "ai_services_other",
          estimate: "ai_services_other_estimate",
        },
        label: "ai_services_other",
        showChangePercentage: true,
      },

      {
        key: { current: "document_ai", estimate: "document_ai_estimate" },
        label: "document_ai",
        showChangePercentage: true,
      },
      {
        key: { current: "fine_tuning", estimate: "fine_tuning_estimate" },
        label: "fine_tuning",
        showChangePercentage: true,
      },
      {
        key: { current: "analyst", estimate: "analyst_estimate" },
        label: "analyst",
        showChangePercentage: true,
      },
      {
        key: { current: "search_daily_aggregate", estimate: "search_daily_aggregate_estimate" },
        label: "search_daily_aggregate",
        showChangePercentage: true,
      },
      {
        key: { current: "cortex_code_cli", estimate: "cortex_code_cli_estimate" },
        label: "cortex_code_cli",
        showChangePercentage: true,
      },
      {
        key: { current: "cortex_agent", estimate: "cortex_agent_estimate" },
        label: "cortex_agent",
        showChangePercentage: true,
      },
      {
        key: { current: "snowflake_intelligence", estimate: "snowflake_intelligence_estimate" },
        label: "snowflake_intelligence",
        showChangePercentage: true,
      },
      // Separate Snowflake billing lines surfaced by the backend after
      // AI-6317 reconciliation. They MUST be in this list — otherwise
      // the rendered components don't sum to `total` (rakuten
      // 2026-04-20 missed cortex_code_snowsight = $37.89, which made
      // the rendered breakdown fall short of the rendered total by
      // exactly that amount).
      {
        key: {
          current: "cortex_code_snowsight",
          estimate: "cortex_code_snowsight_estimate",
        },
        label: "cortex_code_snowsight",
        showChangePercentage: true,
      },
      {
        key: {
          current: "cortex_code_desktop",
          estimate: "cortex_code_desktop_estimate",
        },
        label: "cortex_code_desktop",
        showChangePercentage: true,
      },
      {
        key: { current: "reader_ai_services", estimate: "reader_ai_services_estimate" },
        label: "reader_ai_services",
        showChangePercentage: true,
      },
      {
        key: { current: "reader_ai_inference", estimate: "reader_ai_inference_estimate" },
        label: "reader_ai_inference",
        showChangePercentage: true,
      },
      {
        key: {
          current: "reader_cortex_code_snowsight",
          estimate: "reader_cortex_code_snowsight_estimate",
        },
        label: "reader_cortex_code_snowsight",
        showChangePercentage: true,
      },
    ];

    // If user has no access, only show Cortex fine-tuning and Cortex function usage
    const filteredFields = hasNoAccess
      ? allAiServicesFields.filter(
          field => field.label === "cortex_function" || field.label === "fine_tuning"
        )
      : allAiServicesFields;

    totalCostEntities.ai_services = {
      fields: filteredFields,
    };
  }

  return totalCostEntities;
};
